// Phase 14 — External resources page.
//
// Top-level sidebar section (sits below Library, not nested). Renders the
// taxonomy-driven list of `external_link` entries: each is an icon + a
// trilingual title + an https URL that opens in a new tab. Entries are
// admin-managed via Settings → Taxonomies → External Links.
//
// Read-only view — no CRUD here. Each entry's visual is logo-first:
// meta.logoData (an uploaded base64 image) when it loads, otherwise the
// lucide icon in meta.icon resolved via lucideRegistry.iconByName().

import { useState } from "react";
import { useTranslation } from "../i18n";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { pickEntryLabel } from "../utils/taxonomyMap";
import { iconByName } from "../lib/lucideRegistry";
import { RAD, FS } from "../lib/tokens";

// Entry icon slot — logo-first rendering. A meta.logoData base64 image that
// loads renders as an <img> (object-fit: contain so the logo keeps its
// aspect ratio inside the square slot); absent or broken data falls back to
// the lucide icon (iconByName(meta.icon)), which itself falls back to
// MessageCircle for an unknown name. State-driven swap — the <img> onError
// flips to the icon with no layout change. Slot geometry is unchanged from
// the icon-only version (38×38 chip, 30×30 logo).
function ExternalIcon({ logoData, iconName }: any) {
  const [imgOk, setImgOk] = useState(false);
  const hasLogo = typeof logoData === "string" && logoData.startsWith("data:");
  const Icon = iconByName(iconName);
  const showImg = hasLogo && imgOk;
  return (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 38,
        height: 38,
        borderRadius: RAD.r8,
        flexShrink: 0,
        background: "var(--bs)",
        border: "1px solid var(--br)",
      }}
    >
      {hasLogo && (
        <img
          key={logoData.slice(0, 48)}
          src={logoData}
          alt=""
          onLoad={() => setImgOk(true)}
          onError={() => setImgOk(false)}
          style={{
            width: 30,
            height: 30,
            objectFit: "contain",
            display: showImg ? "block" : "none",
          }}
        />
      )}
      {!showImg && <Icon size={20} style={{ color: "var(--ac)" }} />}
    </span>
  );
}

export default function ExternalView() {
  const { t } = useTranslation();
  // useTaxonomy already excludes archived rows and sorts by order then id.
  const { entries, loading } = useTaxonomy("external_link");

  const card = (e: any) => {
    const url = (e.meta && e.meta.url) || "";
    const title = pickEntryLabel(e);
    const inner = (
      <>
        <ExternalIcon
          logoData={e.meta && e.meta.logoData}
          iconName={e.meta && e.meta.icon}
        />
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 0,
            flex: 1,
          }}
        >
          <span style={{ fontSize: FS.s13, fontWeight: 600, color: "var(--tp)" }}>
            {title}
          </span>
          <span
            style={{
              fontSize: FS.s10,
              color: "var(--tm)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {url || t("external.no_url", { fallback: "No URL configured" })}
          </span>
        </span>
      </>
    );
    const baseStyle = {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "12px 14px",
      textDecoration: "none",
    };
    return url ? (
      <a
        key={e.id}
        className="detail-card"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={baseStyle}
      >
        {inner}
      </a>
    ) : (
      <div key={e.id} className="detail-card" style={{ ...baseStyle, opacity: 0.6 }}>
        {inner}
      </div>
    );
  };

  return (
    <>
      {/* 196 §D.1 [RULED — Laurent, 2026-09-19] — THE TITLE IS PRINTED ONCE.
          A `<p>` stood here printing « Liens externes » (`external.title`)
          under the page header that already prints « Liens externes » off
          the nav label — exactly 195B §D's Archive wart, one line, no
          control on it, so it simply goes; the key keeps its rows, dead. */}
      {loading && entries.length === 0 ? (
        <p style={{ fontSize: FS.s12, color: "var(--tm)" }}>
          {t("common.loading", { fallback: "Loading…" })}
        </p>
      ) : entries.length === 0 ? (
        <div
          className="detail-card"
          style={{ padding: "20px 16px", textAlign: "center" }}
        >
          <p style={{ fontSize: FS.s13, color: "var(--tp)", margin: "0 0 6px" }}>
            {t("external.empty", { fallback: "No external links configured" })}
          </p>
          <p
            style={{
              fontSize: FS.s11,
              color: "var(--tm)",
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {t("external.empty_hint", {
              fallback:
                "An administrator can add entries in Settings → Taxonomies → External Links.",
            })}
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gap: 10,
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          }}
        >
          {entries.map(card)}
        </div>
      )}
    </>
  );
}
